/**
 * FeedbackModal Component
 * Displays essay/expansion feedback one item at a time with navigation
 * Shows familiarity score changes with animations and encouragement messages
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  GateEvaluationResult,
  VocabularyAnalysis,
  FamiliarityChangeItem,
} from '../../types/writing';
import { ClickableText } from '../ClickableWord';
import './FeedbackModal.css';

// Types of feedback items that can be shown
type FeedbackItemType =
  | 'score'
  | 'feedback'
  | 'strengths'
  | 'grammarIssue'
  | 'suggestion'
  | 'improvedVersion'
  | 'aiEncouragement'
  | 'familiarityChange'
  | 'encouragement';

interface FeedbackItem {
  type: FeedbackItemType;
  content: string;
  wordInfo?: FamiliarityChangeItem;
  index?: number;
}

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  result: GateEvaluationResult;
  vocabularyAnalysis?: VocabularyAnalysis;
  onSaveToNotes?: (content: string) => void;
  onFamiliarityUpdateComplete?: () => void;
}

const FeedbackModal: React.FC<FeedbackModalProps> = ({
  isOpen,
  onClose,
  result,
  vocabularyAnalysis,
  onSaveToNotes,
  onFamiliarityUpdateComplete,
}) => {
  const { t } = useTranslation(['writing', 'common']);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [items, setItems] = useState<FeedbackItem[]>([]);
  const [animatingFamiliarity, setAnimatingFamiliarity] = useState(false);

  // Reset to first item whenever modal opens
  useEffect(() => {
    if (isOpen) {
      setCurrentIndex(0);
    }
  }, [isOpen]);

  // Build the list of feedback items
  useEffect(() => {
    const feedbackItems: FeedbackItem[] = [];

    // 1. Main feedback (skip score page)
    if (result.feedback) {
      feedbackItems.push({
        type: 'feedback',
        content: result.feedback,
      });
    }

    // 2. Strengths
    if (result.strengths && result.strengths.length > 0) {
      feedbackItems.push({
        type: 'strengths',
        content: result.strengths.join('\n'),
      });
    }

    // 3. Grammar issues (one by one)
    if (result.grammarIssues && result.grammarIssues.length > 0) {
      result.grammarIssues.forEach((issue, index) => {
        feedbackItems.push({
          type: 'grammarIssue',
          content: issue,
          index,
        });
      });
    }

    // 4. Suggestions (one by one)
    if (result.suggestions && result.suggestions.length > 0) {
      result.suggestions.forEach((suggestion, index) => {
        feedbackItems.push({
          type: 'suggestion',
          content: suggestion,
          index,
        });
      });
    }

    // 5. Improved version
    if (result.improvedVersion) {
      feedbackItems.push({
        type: 'improvedVersion',
        content: result.improvedVersion,
      });
    }

    // 6. AI encouragement
    if (result.encouragement) {
      feedbackItems.push({
        type: 'aiEncouragement',
        content: result.encouragement,
      });
    }

    // 7. Familiarity changes (one by one, with animation)
    if (vocabularyAnalysis?.familiarityChanges && vocabularyAnalysis.familiarityChanges.length > 0) {
      vocabularyAnalysis.familiarityChanges.forEach((change, index) => {
        feedbackItems.push({
          type: 'familiarityChange',
          content: change.word,
          wordInfo: change,
          index,
        });
      });
    }

    // 7. Final encouragement
    if (vocabularyAnalysis?.encouragementMessage) {
      feedbackItems.push({
        type: 'encouragement',
        content: vocabularyAnalysis.encouragementMessage,
      });
    }

    setItems(feedbackItems);
    setCurrentIndex(0);
  }, [result, vocabularyAnalysis]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;
      if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        goToPrevious();
      } else if (e.key === 'ArrowRight' || e.key === 'ArrowDown' || e.key === ' ') {
        goToNext();
      } else if (e.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
    // goToNext, goToPrevious, and onClose are intentionally excluded to avoid
    // re-registering the listener on every render (they use currentIndex internally)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, currentIndex, items.length]);

  const goToPrevious = useCallback(() => {
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
    }
  }, [currentIndex]);

  const goToNext = useCallback(() => {
    if (currentIndex < items.length - 1) {
      setCurrentIndex(prev => prev + 1);
    } else {
      // Last item, close modal
      onFamiliarityUpdateComplete?.();
      onClose();
    }
  }, [currentIndex, items.length, onClose, onFamiliarityUpdateComplete]);

  // Trigger familiarity animation when showing familiarity change item
  useEffect(() => {
    const currentItem = items[currentIndex];
    if (currentItem?.type === 'familiarityChange') {
      setAnimatingFamiliarity(true);
      const timer = setTimeout(() => setAnimatingFamiliarity(false), 1000);
      return () => clearTimeout(timer);
    }
  }, [currentIndex, items]);

  if (!isOpen || items.length === 0) return null;

  const currentItem = items[currentIndex];
  const progress = ((currentIndex + 1) / items.length) * 100;

  const renderItemContent = () => {
    switch (currentItem.type) {
      case 'score':
        return (
          <div className="feedback-item feedback-item--score">
            <div className={`score-circle ${result.passed ? 'passed' : 'failed'}`}>
              <span className="score-value">{currentItem.content}</span>
              <span className="score-label">
                {result.passed ? t('passed') : t('needsImprovement')}
              </span>
            </div>
          </div>
        );

      case 'feedback':
        return (
          <div className="feedback-item feedback-item--feedback">
            <div className="feedback-icon">💬</div>
            <div className="feedback-content">
              <ClickableText text={currentItem.content} />
            </div>
            {onSaveToNotes && (
              <button
                className="save-note-btn"
                onClick={() => onSaveToNotes(currentItem.content)}
                title={t('saveToNotes')}
              >
                📝 {t('saveToNotes')}
              </button>
            )}
          </div>
        );

      case 'strengths':
        return (
          <div className="feedback-item feedback-item--strengths">
            <div className="feedback-icon">💪</div>
            <div className="feedback-label">{t('strengths')}</div>
            <ul className="strengths-list">
              {currentItem.content.split('\n').map((s, i) => (
                <li key={i}>{s}</li>
              ))}
            </ul>
          </div>
        );

      case 'grammarIssue': {
        // Parse 「wrong」→「correct」— explanation ¶ example format
        const grammarMatch = currentItem.content.match(/「([^」]*)」→「([^」]*)」[—-]\s*([\s\S]*)/);
        let explanation = '';
        let example = '';
        if (grammarMatch) {
          const parts = grammarMatch[3].split('¶');
          explanation = parts[0].trim();
          example = parts[1]?.trim() || '';
        }
        return (
          <div className="feedback-item feedback-item--grammar">
            <div className="feedback-label">
              {t('grammarIssue')} #{(currentItem.index || 0) + 1}
            </div>
            <div className="feedback-icon">⚠️</div>
            {grammarMatch ? (
              <div className="feedback-content grammar-parsed">
                <div className="grammar-wrong">
                  <span className="grammar-tag grammar-tag--wrong">✗</span>
                  <ClickableText text={grammarMatch[1]} />
                </div>
                <div className="grammar-arrow">↓</div>
                <div className="grammar-correct">
                  <span className="grammar-tag grammar-tag--correct">✓</span>
                  <ClickableText text={grammarMatch[2]} />
                </div>
                <div className="grammar-explanation">
                  {explanation}
                </div>
                {example && (
                  <div className="grammar-example">
                    <span className="grammar-example-label">e.g.</span> {example}
                  </div>
                )}
              </div>
            ) : (
              <div className="feedback-content">
                <ClickableText text={currentItem.content} />
              </div>
            )}
            {onSaveToNotes && (
              <button
                className="save-note-btn"
                onClick={() => {
                  const formatted = grammarMatch
                    ? `✗ ${grammarMatch[1]}\n✓ ${grammarMatch[2]}\n${explanation}${example ? '\ne.g. ' + example : ''}`
                    : currentItem.content;
                  onSaveToNotes(formatted);
                }}
                title={t('saveToNotes')}
              >
                📝 {t('saveToNotes')}
              </button>
            )}
          </div>
        );
      }

      case 'suggestion':
        return (
          <div className="feedback-item feedback-item--suggestion">
            <div className="feedback-label">
              {t('suggestion')} #{(currentItem.index || 0) + 1}
            </div>
            <div className="feedback-icon">💡</div>
            <div className="feedback-content">
              <ClickableText text={currentItem.content} />
            </div>
            {onSaveToNotes && (
              <button
                className="save-note-btn"
                onClick={() => onSaveToNotes(currentItem.content)}
                title={t('saveToNotes')}
              >
                📝 {t('saveToNotes')}
              </button>
            )}
          </div>
        );

      case 'improvedVersion':
        return (
          <div className="feedback-item feedback-item--improved">
            <div className="feedback-label">{t('improvedVersion')}</div>
            <div className="feedback-icon">✨</div>
            <div className="feedback-content improved-text">
              <ClickableText text={currentItem.content} />
            </div>
            {onSaveToNotes && (
              <button
                className="save-note-btn"
                onClick={() => onSaveToNotes(currentItem.content)}
                title={t('saveToNotes')}
              >
                📝 {t('saveToNotes')}
              </button>
            )}
          </div>
        );

      case 'aiEncouragement':
        return (
          <div className="feedback-item feedback-item--ai-encouragement">
            <div className="feedback-icon">🎯</div>
            <div className="ai-encouragement-content">
              {currentItem.content}
            </div>
          </div>
        );

      case 'familiarityChange':
        const change = currentItem.wordInfo;
        if (!change) return null;
        return (
          <div className="feedback-item feedback-item--familiarity">
            <div className="feedback-label">{t('vocabularyProgress')}</div>
            <div className="familiarity-card">
              <div className="familiarity-word">{change.word}</div>
              <div className="familiarity-change-display">
                <div className="familiarity-bar-container">
                  <div
                    className="familiarity-bar familiarity-bar--previous"
                    style={{ width: `${change.previousFamiliarity}%` }}
                  />
                  <div
                    className={`familiarity-bar familiarity-bar--new ${animatingFamiliarity ? 'animating' : ''}`}
                    style={{ width: `${change.newFamiliarity}%` }}
                  />
                </div>
                <div className="familiarity-values">
                  <span className="familiarity-previous">{change.previousFamiliarity}</span>
                  <span className="familiarity-arrow">→</span>
                  <span className="familiarity-new">{change.newFamiliarity}</span>
                  <span className={`familiarity-delta ${change.change > 0 ? 'positive' : ''}`}>
                    +{change.change}
                  </span>
                </div>
              </div>
              {change.isNew && (
                <div className="familiarity-badge new-word">{t('newWord')}</div>
              )}
              {change.encouragement && (
                <div className="familiarity-encouragement">
                  🎉 {change.encouragement}
                </div>
              )}
              <div className="familiarity-reason">
                {change.reason === 'correct_existing' && t('usedCorrectlyExisting')}
                {change.reason === 'error_existing' && t('usedWithErrorExisting')}
                {change.reason === 'correct_new' && t('usedCorrectlyNew')}
                {change.reason === 'error_new' && t('usedWithErrorNew')}
              </div>
            </div>
          </div>
        );

      case 'encouragement':
        return (
          <div className="feedback-item feedback-item--encouragement">
            <div className="encouragement-icon">🌟</div>
            <div className="encouragement-content">
              {currentItem.content}
            </div>
            <div className="total-gain">
              {t('totalFamiliarityGain', { points: vocabularyAnalysis?.totalFamiliarityGain || 0 })}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="feedback-modal-overlay" onClick={onClose}>
      <div className="feedback-modal" onClick={e => e.stopPropagation()}>
        {/* Progress bar */}
        <div className="feedback-progress">
          <div className="feedback-progress-bar" style={{ width: `${progress}%` }} />
          <span className="feedback-progress-text">
            {currentIndex + 1} / {items.length}
          </span>
        </div>

        {/* Content area */}
        <div className="feedback-modal-content">
          {renderItemContent()}
        </div>

        {/* Navigation */}
        <div className="feedback-navigation">
          <button
            className="nav-btn nav-btn--prev"
            onClick={goToPrevious}
            disabled={currentIndex === 0}
          >
            ← {t('previous')}
          </button>
          <button
            className="nav-btn nav-btn--next"
            onClick={goToNext}
          >
            {currentIndex === items.length - 1 ? t('finish') : t('next')} →
          </button>
        </div>

        {/* Close button */}
        <button className="feedback-close-btn" onClick={onClose}>
          ×
        </button>
      </div>
    </div>
  );
};

export default FeedbackModal;
